import { useMemo } from "react";
import { Form, Field } from "react-final-form";
import Icon from "components/Icon";
import { AdminSelectAdapter } from "components/select";
import client from "libs/client";
import { getApiErrorMessage } from "libs/utils";
import type { CompetitionEditionRowResponse } from "libs/apiClient";
import { useToast } from "ui-kit";
import type { SelectOption } from "./competitionOptions";

type CreateSeasonFormValues = {
	season: SelectOption | null;
};

type CreateSeasonFormErrors = {
	season?: string;
};

export type CompetitionEditionCreateSeasonModalProps = {
	competitionName: string;
	editions: CompetitionEditionRowResponse[];
	onClose: () => void;
	onCreated: () => void | Promise<void>;
};

function extractMutationErrorMessage(error: unknown): string {
	if (error && typeof error === "object") {
		const err = error as {
			json?: string | { message?: string };
			message?: string;
		};
		if (typeof err.json === "string") {
			return err.json;
		}
		if (err.json?.message) {
			return err.json.message;
		}
		if (err.message) {
			return err.message;
		}
	}
	return "Something went wrong";
}

function competitionInitials(name: string): string {
	const words = name
		.trim()
		.split(/\s+/)
		.filter((word) => word !== "");
	if (words.length === 0) {
		return "?";
	}
	if (words.length === 1) {
		return words[0].slice(0, 2).toUpperCase();
	}
	return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
}

function formatEditionLabel(seasonName: string, groupLabel: string | null | undefined): string {
	if (groupLabel != null && groupLabel !== "") {
		return `${seasonName} · ${groupLabel}`;
	}
	return seasonName;
}

function validateCreateSeasonForm(values: CreateSeasonFormValues): CreateSeasonFormErrors {
	const errors: CreateSeasonFormErrors = {};
	if (values.season == null || values.season.value === "") {
		errors.season = "New season is required";
	}
	return errors;
}

export function CompetitionEditionCreateSeasonModal({
	competitionName,
	editions,
	onClose,
	onCreated,
}: CompetitionEditionCreateSeasonModalProps) {
	const { addSuccessToast, addErrorToast } = useToast();

	const copyMutation = client.postCompetitionEditionCopyToSeason.useMutation();
	const { data: seasonsResponse } = client.getSeasons.useQuery({ retry: false });

	const sourceSeasonId = editions[0]?.seasonId;
	const sourceSeasonName = editions[0]?.seasonName ?? "-";
	const editionCount = editions.length;

	const seasonOptions = useMemo((): SelectOption[] => {
		const seasons = seasonsResponse?.data?.items ?? [];
		return seasons
			.filter(
				(season) =>
					season.id != null &&
					season.name != null &&
					season.id !== sourceSeasonId,
			)
			.map((season) => ({
				value: String(season.id),
				label: season.name ?? String(season.id),
			}));
	}, [seasonsResponse, sourceSeasonId]);

	const initialValues = useMemo((): CreateSeasonFormValues => {
		const firstOption = seasonOptions[0] ?? null;
		return {
			season: firstOption,
		};
	}, [seasonOptions]);

	const initials = competitionInitials(competitionName);

	const handleSubmit = async (values: CreateSeasonFormValues) => {
		if (values.season == null) {
			return;
		}

		const seasonId = Number(values.season.value);
		if (Number.isFinite(seasonId) === false || seasonId <= 0) {
			addErrorToast("Invalid season");
			return;
		}

		const editionIds = editions
			.map((edition) => edition.id)
			.filter((editionId): editionId is number => editionId != null);

		if (editionIds.length === 0) {
			addErrorToast("No editions selected");
			return;
		}

		try {
			await copyMutation.mutateAsync({
				body: {
					editionIds,
					seasonId,
				},
			});
			addSuccessToast(
				`Created ${editionIds.length} edition${editionIds.length === 1 ? "" : "s"}`,
			);
			await onCreated();
			onClose();
		} catch (error: unknown) {
			addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
		}
	};

	return (
		<div className="modal open">
			<span className="modal__backdrop" onClick={onClose} />
			<div className="modal__container competitions-create-season-modal">
				<span className="modal__close" onClick={onClose}>
					<Icon icon="cross" />
				</span>
				<div className="modal__title">Create new season</div>
				<p className="competitions-create-season-modal__subtitle">
					{editionCount} edition{editionCount === 1 ? "" : "s"} selected across 1 competition
				</p>
				<div className="modal__body">
					<Form
						onSubmit={handleSubmit}
						initialValues={initialValues}
						validate={validateCreateSeasonForm}
						keepDirtyOnReinitialize
						render={({ handleSubmit: submitForm, errors, touched, submitFailed, values }) => {
							const targetSeasonLabel = values.season?.label ?? "—";

							return (
								<form onSubmit={submitForm}>
									<div className="field-group">
										<label className="required">New season</label>
										<Field
											name="season"
											component={AdminSelectAdapter}
											options={seasonOptions}
										/>
										{(touched?.season || submitFailed) && errors?.season ? (
											<span className="form-error">{errors.season}</span>
										) : null}
									</div>

									<div className="competitions-create-season-preview">
										<div className="competitions-create-season-preview__header">
											<span
												className="competitions-create-season-preview__initials"
												aria-hidden="true"
											>
												{initials}
											</span>
											<span className="competitions-create-season-preview__name">
												{competitionName}
											</span>
											<span className="competitions-create-season-preview__count">
												{editionCount} edition(s)
											</span>
										</div>
										<ul className="competitions-create-season-preview__list">
											{editions.map((edition) => {
												const editionKey =
													edition.id ??
													`${edition.seasonId}-${edition.groupLabel}-${edition.phaseLabel}`;
												const sourceLabel = formatEditionLabel(
													edition.seasonName ?? sourceSeasonName,
													edition.groupLabel,
												);
												const targetLabel = formatEditionLabel(
													targetSeasonLabel,
													edition.groupLabel,
												);
												const teamCount = edition.teamCount ?? 0;

												return (
													<li
														key={editionKey}
														className="competitions-create-season-preview__row"
													>
														<span
															className="icon icon-new icon-16 icon-dark-grey icon-calendar"
															aria-hidden="true"
														/>
														<span className="competitions-create-season-preview__mapping">
															{sourceLabel}
															{" → "}
															<strong>{targetLabel}</strong>
														</span>
														<span className="competitions-create-season-preview__teams">
															{teamCount} team{teamCount === 1 ? "" : "s"} to enroll
														</span>
													</li>
												);
											})}
										</ul>
									</div>

									<div className="form-row competitions-form-actions">
										<button type="button" className="btn btn--cancel" onClick={onClose}>
											Cancel
										</button>
										<button
											type="submit"
											className="btn btn-light-blue"
											disabled={copyMutation.isLoading || seasonOptions.length === 0}
										>
											Create {editionCount} edition{editionCount === 1 ? "" : "s"}
										</button>
									</div>
								</form>
							);
						}}
					/>
				</div>
			</div>
		</div>
	);
}

export default CompetitionEditionCreateSeasonModal;
